import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import ProfileCard from '@/components/profile/ProfileCard';
import { EscortProfile } from '@/types';
import { HeartCrack } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useProfileImages } from '@/hooks/useProfileImages';
import { supabase } from '@/integrations/supabase/client';
import { getEscorts } from '@/data/mockData';

const Favorites = () => {
  const [favoriteProfiles, setFavoriteProfiles] = useState<EscortProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { 
    profileImages, 
    isGenerating, 
    generateImageForProfile
  } = useProfileImages(favoriteProfiles);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const loadFavorites = async () => {
      setLoading(true);
      try {
        console.log('Loading favorites for user:', user.id);
        
        let favoriteIds: string[] = [];
        
        const { data: supabaseFavorites, error: favoritesError } = await supabase
          .from('user_favorites')
          .select('profile_id')
          .eq('user_id', user.id);
        
        if (favoritesError) {
          console.error('Error loading favorites from Supabase:', favoritesError);
          throw favoritesError;
        }
        
        if (supabaseFavorites && supabaseFavorites.length > 0) {
          favoriteIds = supabaseFavorites.map(fav => fav.profile_id);
          console.log('Loaded favorite IDs from Supabase:', favoriteIds);
        } else {
          const savedFavorites = localStorage.getItem('favorites');
          
          if (savedFavorites) {
            favoriteIds = JSON.parse(savedFavorites);
            console.log('Loaded favorite IDs from localStorage:', favoriteIds);
            
            if (favoriteIds.length > 0) {
              try {
                const rows = favoriteIds.map(profileId => ({
                  user_id: user.id,
                  profile_id: profileId
                }));
                
                const { error: insertError } = await supabase
                  .from('user_favorites')
                  .insert(rows, { onConflict: 'user_id, profile_id' });
                
                if (insertError) {
                  console.error('Error migrating favorites to Supabase:', insertError);
                } else {
                  console.log('Successfully migrated favorites to Supabase');
                }
              } catch (migrationError) {
                console.error('Error during favorites migration:', migrationError);
              }
            }
          }
        }
        
        setFavorites(new Set(favoriteIds));
        
        if (favoriteIds.length === 0) {
          setFavoriteProfiles([]);
          setLoading(false);
          return;
        }
        
        try {
          console.log('Fetching escort profiles from Supabase');
          
          const { data: escortProfiles, error: escortsError } = await supabase
            .from('escort_profiles')
            .select(`
              id,
              display_name,
              age,
              bio_short,
              bio_full,
              city,
              country,
              latitude,
              longitude,
              gender,
              height,
              weight,
              body_type,
              hair_color,
              eye_color,
              ethnicity,
              nationality,
              measurements,
              cup_size,
              intimate_area,
              has_tattoos,
              has_piercings,
              smoking,
              drinking,
              sexual_orientation,
              is_verified,
              is_online,
              is_touring
            `)
            .in('id', favoriteIds);
          
          if (escortsError) {
            console.error('Error fetching escort profiles:', escortsError);
            throw escortsError;
          }
          
          if (escortProfiles && escortProfiles.length > 0) {
            console.log('Loaded escort profiles from Supabase:', escortProfiles.length);
            
            const transformedProfiles: EscortProfile[] = escortProfiles.map(profile => ({
              id: profile.id,
              userId: profile.user_id,
              displayName: profile.display_name,
              age: profile.age,
              bio: {
                short: profile.bio_short || '',
                full: profile.bio_full || ''
              },
              location: {
                city: profile.city || '',
                country: profile.country || '',
                latitude: profile.latitude,
                longitude: profile.longitude
              },
              attributes: {
                gender: profile.gender,
                height: profile.height,
                weight: profile.weight,
                bodyType: profile.body_type,
                hairColor: profile.hair_color,
                eyeColor: profile.eye_color,
                ethnicity: profile.ethnicity,
                nationality: profile.nationality,
                measurements: profile.measurements,
                cupSize: profile.cup_size,
                intimateArea: profile.intimate_area,
                tattoos: profile.has_tattoos,
                piercings: profile.has_piercings,
                smoking: profile.smoking,
                drinking: profile.drinking,
                sexualOrientation: profile.sexual_orientation,
                languages: []
              },
              services: [],
              rates: [],
              isVerified: profile.is_verified,
              isOnline: profile.is_online,
              isTouring: profile.is_touring,
              tourDates: []
            }));
            
            setFavoriteProfiles(transformedProfiles);
            setLoading(false);
            return;
          }
        } catch (supabaseError) {
          console.error('Error with Supabase escort profiles:', supabaseError);
          // Continue to fallback
        }
        
        console.log('Falling back to mock data for escort profiles');
        const allProfiles = await getEscorts();
        const favorites = allProfiles.filter(profile => favoriteIds.includes(profile.id));
        
        console.log('Filtered favorite profiles from mock data:', favorites.length);
        setFavoriteProfiles(favorites);
      } catch (error) {
        console.error('Error loading favorites:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger vos profils favoris.',
          variant: 'destructive',
        });
        
        setFavoriteProfiles([]);
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [user, navigate, toast]);

  const handleToggleFavorite = async (profileId: string) => {
    try {
      setFavoriteProfiles(prev => prev.filter(profile => profile.id !== profileId));
      
      const newFavorites = new Set(favorites);
      newFavorites.delete(profileId);
      setFavorites(newFavorites);
      
      localStorage.setItem('favorites', JSON.stringify([...newFavorites]));
      
      if (user) {
        const { error } = await supabase
          .from('user_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('profile_id', profileId);
        
        if (error) {
          console.error('Error removing favorite from Supabase:', error);
          throw error;
        }
      }
      
      toast({
        title: 'Supprimé des favoris',
        description: 'Le profil a été supprimé de vos favoris.',
      });
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le favori. Veuillez réessayer.',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateImage = async (profile: EscortProfile, showToast?: boolean) => {
    return generateImageForProfile(profile, showToast);
  };

  return (
    <Layout>
      <div className="py-8 px-4 bg-muted/30 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Vos Profils Favoris</h1>
          
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-pulse">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-card rounded-lg h-[400px]"></div>
              ))}
            </div>
          ) : favoriteProfiles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {favoriteProfiles.map((profile) => (
                <ProfileCard 
                  key={profile.id} 
                  profile={profile}
                  isFavorite={true}
                  onToggleFavorite={handleToggleFavorite}
                  profileImages={profileImages}
                  isGeneratingImage={Boolean(isGenerating.get(profile.id))}
                  onGenerateImage={() => handleGenerateImage(profile, true)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <HeartCrack size={48} className="mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Aucun Favori</h2>
              <p className="text-muted-foreground mb-6">
                Vous n'avez pas encore ajouté de profils à vos favoris.
              </p>
              <Button 
                onClick={() => navigate('/explore')}
                className="bg-pink-gradient hover:opacity-90"
              >
                Explorer les Profils
              </Button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Favorites;
